import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { PublicDemoBundle } from '../types'
import { useI18n } from '../i18n'

type PublicDemoState = {
  data: PublicDemoBundle | null
  error: string | null
}

const PublicDemoContext = createContext<PublicDemoState | null>(null)

export function PublicDemoProvider({ children }: { children: ReactNode }) {
  const { copy } = useI18n()
  const [state, setState] = useState<PublicDemoState>({ data: null, error: null })

  useEffect(() => {
    const controller = new AbortController()
    fetch('/data/commerce-public-demo-v1.json', { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Public evidence bundle failed to load (${response.status})`)
        return response.json() as Promise<PublicDemoBundle>
      })
      .then((data) => setState({ data, error: null }))
      .catch((reason: Error) => {
        if (reason.name !== 'AbortError') setState({ data: null, error: reason.message })
      })
    return () => controller.abort()
  }, [])

  if (state.error) {
    return <main className="grid min-h-screen place-items-center bg-canvas p-6"><p className="rounded-lg bg-white p-6 text-coral shadow-surface">{state.error}</p></main>
  }
  if (!state.data) {
    return <main className="grid min-h-screen place-items-center bg-brand text-white"><div className="loading-mark" aria-label={copy.common.loading} /></main>
  }

  return <PublicDemoContext.Provider value={state}>{children}</PublicDemoContext.Provider>
}

export function usePublicDemo() {
  const context = useContext(PublicDemoContext)
  if (!context?.data) throw new Error('usePublicDemo must be used inside PublicDemoProvider')
  return context.data
}
